import React from 'react';
import PropTypes from 'prop-types';
import { orderBy, get } from 'lodash';
import { css } from '@bd/bdui-styles/emotion';
import { SanitizeHtml } from '@bd/bdcore-utilities';
import CommunityDetailHeader from './CommunityDetailHeader';
import HomeCard from '../../components/HomeCard';
import PlanCard from '../../components/PlanCard';
import { colors, typography } from '../../styles';
import SitePlanModal from './SitePlanModal';
import DetailDescription from './DetailDescription';
import DetailPhotoGallery from './DetailPhotoGallery';
import CommunityFeatures from './CommunityFeatures';

const styles = css`
  .DetailSection {
    padding: 90px 0;
    img {
      max-width: 100%;
    }
  }
  .DetailSection_heading {
    text-transform: uppercase;
    font-family: ${typography.Serif};
    font-size: 24px;
    font-weight: 300;
    color: ${colors.color5};
    position: relative;
    text-align: center;
    margin: 0 0 60px 0;
    span {
      font-weight: 900;
      color: ${colors.color5Darker};
    }
    &:after {
      content: '';
      width: 180px;
      height: 5px;
      bottom: -30px;
      background-color: ${colors.color2};
      left: 0;
      right: 0;
      margin: 0 auto;
      position: absolute;
      display: block;
    }
    @media (min-width: 992px) {
      margin: 0 0 70px 0;
      font-size: 36px;
    }
  }
  .SitePlan_banner {
    background-image: url('/images/contact-cta-bg.svg');
    background-size: cover;
    padding: 45px;
    text-align: center;
  }
  .SitePlan_bannerTitle {
    font-weight: 800;
    font-size: 18px;
    color: ${colors.color5Lighter};
    letter-spacing: 0;
    text-align: center;
    text-transform: uppercase;
    margin: 0 0 15px 0;
    @media (min-width: 992px) {
      font-size: 21px;
    }
  }
  .SitePlan_bannerPostTitle {
    font-weight: 200;
    font-size: 42px;
    color: ${colors.color1};
    @media (min-width: 992px) {
      font-size: 70px;
    }
  }
  .POIMap_wrapper {
    display: flex;
    flex-direction: row-reverse;
  }
  .POIMap_inner {
    width: 30%;
    padding: 50px;
    background-color: ${colors.color3Lighter};
    overflow: hidden;
    overflow-y: scroll;
    height: 600px;
  }
  .POIMap_categories {
    list-style: none;
    padding: 0;
    text-align: left;
    li {
      margin: 0 0 15px 0;
      span {
        cursor: pointer;
        color: ${colors.color5};
      }
    }
    img {
      margin: 0 10px 0 0;
    }
    ul {
      list-style: none;
      padding: 10px 0 0 10px;
      li {
        font-size: 13px;
        font-weight: 600;
      }
    }
  }
  .POIMap_mapWrapper {
    position: relative;
    z-index: 0;
    width: 70%;
    .leaflet-container {
      height: 600px;
    }
  }
`;

function CommunityDetailPage({ community, homes, plans }) {
  return (
    <div className={styles}>
      <CommunityDetailHeader />
      <DetailDescription />
      { get(community, 'photos', []).length > 0 &&
        <div className="DetailSection">
          <h3 className="DetailSection_heading" id="photo"><span>Photo</span> Gallery</h3>
          <DetailPhotoGallery />
        </div>
      }
      {
        homes.length > 0 &&
        <div className="DetailSection">
          <h3 className="DetailSection_heading" id="homes"><span>Available</span> Homes</h3>
          <div className="container">
            <div className="row py-4 justify-content-center">
              {
                orderBy(homes, 'sqft', 'desc').map(home => (
                  <div key={home._id} className="col-lg-6 col-xl-4">
                    <HomeCard home={home} />
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      }
      {
        plans.length > 0 &&
        <div className="DetailSection">
          <h3 className="DetailSection_heading" id="plans"><span>Available</span> Floor Plans</h3>
          <div className="container">
            <div className="row py-4 justify-content-center">
              {
                orderBy(plans, 'name').map(plan => (
                  <div className="col-lg-6 col-xl-4" key={plan._id}>
                    <PlanCard plan={plan} />
                  </div>
                ))
              }
            </div>
          </div>
        </div>
      }
      {
        get(community, 'videoEmbeds', []).length > 0 &&
        <div className="container pb-5" id="video">
          <div className="SitePlan_banner">
            <h3 className="SitePlan_bannerTitle">
              {get(community, 'name')}<br />
              <span className="SitePlan_bannerPostTitle">Video Gallery</span>
            </h3>
            <div className="row">
              {
                  get(community, 'videoEmbeds', []).map(video => (
                    <div
                      className="col-lg-6 mb-4 mx-auto"
                      key={video}
                    >
                      <div className="embed-responsive embed-responsive-16by9" dangerouslySetInnerHTML={SanitizeHtml(video)} />
                    </div>
                  ))
                }
            </div>
          </div>
        </div>
      }
      {
        get(community, 'zplatpublished') &&
        <div className="container">
          <div className="SitePlan_banner">
            <h3 className="SitePlan_bannerTitle" id="site-plan">
              {get(community, 'name')}<br />
              <span className="SitePlan_bannerPostTitle">Site Plan</span>
            </h3>
            <SitePlanModal community={community} homes={homes} buttonClassName="btn">
              View Lots
            </SitePlanModal>
          </div>
        </div>
      }
      {
        get(community, 'features', []).length > 0 &&
        <div className="DetailSection">
          <h3 className="DetailSection_heading" id="features"><span>Included</span> Features</h3>
          <CommunityFeatures />
        </div>
      }
    </div>
  );
}

CommunityDetailPage.propTypes = {
  community: PropTypes.object.isRequired,
  plans: PropTypes.arrayOf(PropTypes.object).isRequired,
  homes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default CommunityDetailPage;



// WEBPACK FOOTER //
// ./src/pages/CommunityDetail/CommunityDetailPage.js